// diffPlan — the pure differ for the Platform Update Framework's converge step.
// Compares the target release manifest against the installed-state stamp and
// emits the ordered set of changed-only component actions (plus what was
// skipped and why). No I/O.
//
// applyPlan — the driver-agnostic orchestrator that executes an ApplyPlan via
// injected per-component executors (ConvergeDeps), stamping progress after
// each component so a failed converge is resumable.

import * as fs from "node:fs";
import * as path from "node:path";
import { parse as parseYaml } from "yaml";
import type { TokenCredential } from "@azure/identity";
import { productVisibilityFor } from "@m8t-stack/api-contract";
import type { ReleaseManifest } from "./release-manifest.js";
import type { InstalledStamp, ImageStampEntry, PersonaStampEntry } from "./platform-stamp.js";
import type { GatewayImageAdoption } from "@m8t-stack/platform-release";
import type { RepoTree } from "./release-content.js";
import { runAz } from "./az.js";
import { fetchPublicTags, planUpdate, parseContainerAppResourceId, assertGatewayDigest, gatewayRefFromComponent, DEFAULT_IMAGE_REPO } from "./platform-update.js";
import { runBicepDeployment, buildBicepParams, resolveFoundryResourceId, resolveAcrPullIdentity, resolveAcrResourceId } from "./deploy.js";
import type { FoundryTracingMode, BicepParams } from "./deploy.js";
import { getAgentVersion } from "./foundry-agent-get.js";
import type { AgentDefinition, AgentVersionRecord } from "./foundry-agent-get.js";
import { stagePublicImageIfNeeded } from "./image-staging.js";
import { createAgentVersion } from "./foundry-agent-version.js";
import type { FoundryProject } from "./foundry-project.js";
import { LocalCliError } from "./errors.js";
import { renderPersonaBody } from "./persona-render.js";
import { readAgentYaml } from "./agent-yaml.js";
import { composeInstructions, resolveFillableValues, isNoop } from "./persona-compose.js";
import { equivalentToolDefinition, personaFoundryTools, type PersonaFrontmatter } from "./persona-contract.js";
import { readLiveGatewayImage } from "./gateway-live-params.js";
import { rollAdoptedGateway } from "./gateway-adoption.js";

export type ComponentKey = "infra" | "gateway" | "codingAgent" | "azureExecutor" | "personas" | "brainSeeds";
const ORDER: ComponentKey[] = ["infra", "gateway", "codingAgent", "azureExecutor", "personas", "brainSeeds"];

/** Stable digest of the manifest's per-seed subtree SHAs — the drift key for brainSeeds. */
export function seedSetRev(manifest: ReleaseManifest): string {
  const items = manifest.components.brainSeeds.items;
  const parts: string[] = [];
  for (const name of Object.keys(items).sort()) {
    const subs = items[name].subtrees;
    for (const sub of Object.keys(subs).sort()) parts.push(`${name}:${sub}:${subs[sub] ?? "-"}`);
  }
  return parts.join("|");
}

export interface ComponentAction {
  component: ComponentKey;
  personaName?: string;
  reason: "changed" | "adopt" | "forced";
  from: string | null;
  to: string;
  gatewayAdoption?: GatewayImageAdoption;
}
export interface SkipEntry {
  component: ComponentKey;
  personaName?: string;
  reason: "external" | "up-to-date" | "only-filter" | "deferred";
}
export interface ApplyPlan {
  targetVersion: string;
  previousVersion: string | null;
  actions: ComponentAction[];
  skipped: SkipEntry[];
}

export function infraTargetSha(manifest: ReleaseManifest, tree: RepoTree): string {
  return manifest.components.infra?.treeSha ?? tree.treeShaOf("deploy") ?? "";
}

export function diffPlan(
  manifest: ReleaseManifest,
  stamp: InstalledStamp | null,
  tree: RepoTree,
  opts: { only?: ComponentKey; forceInfra?: boolean; skipInfra?: boolean } = {},
): ApplyPlan {
  const actions: ComponentAction[] = [];
  const skipped: SkipEntry[] = [];
  const adopt = stamp === null;
  const want = (k: ComponentKey) => opts.only === undefined || opts.only === k;

  for (const component of ORDER) {
    if (!want(component)) {
      skipped.push({ component, reason: "only-filter" });
      continue;
    }

    if (component === "infra") {
      if (opts.skipInfra) {
        skipped.push({ component, reason: "up-to-date" });
        continue;
      }
      const to = infraTargetSha(manifest, tree);
      const from = stamp?.components.infra.treeSha ?? null;
      // --force-infra runs the real bicep apply; adopt records a baseline (no bicep).
      // A NON-forced change is DEFERRED to the installer-image converge (it owns infra):
      // reconstructing BicepParams here — the `suffix` in particular — risks provisioning
      // duplicate resources, so the CLI path warns loudly instead of silently claiming
      // convergence. `applyInfra` treats reason "adopt" as record-only, "forced" as a real deploy.
      if (opts.forceInfra) actions.push({ component, reason: "forced", from, to });
      else if (adopt) actions.push({ component, reason: "adopt", from, to });
      else if (from !== to) skipped.push({ component, reason: "deferred" });
      else skipped.push({ component, reason: "up-to-date" });
      continue;
    }

    if (component === "personas") {
      for (const [name, item] of Object.entries(manifest.components.personas.items)) {
        const from = stamp?.components.personas[name]?.treeSha ?? null;
        if (adopt) actions.push({ component, personaName: name, reason: "adopt", from, to: item.treeSha });
        else if (from !== item.treeSha) actions.push({ component, personaName: name, reason: "changed", from, to: item.treeSha });
        else skipped.push({ component, personaName: name, reason: "up-to-date" });
      }
      continue;
    }

    if (component === "brainSeeds") {
      const to = seedSetRev(manifest);
      const from = stamp?.components.brainSeeds?.seedRev ?? null;
      const targeted = opts.only === "brainSeeds"; // explicit target always delivers (markers decide per-repo no-ops)
      if (adopt) actions.push({ component, reason: "adopt", from, to });
      else if (targeted) actions.push({ component, reason: from === to ? "forced" : "changed", from, to });
      else if (from !== to) actions.push({ component, reason: "changed", from, to });
      else skipped.push({ component, reason: "up-to-date" });
      continue;
    }

    // image components: gateway, codingAgent, azureExecutor
    const img = manifest.components[component];
    const cur = stamp?.components[component];
    if (cur?.state === "external") {
      skipped.push({ component, reason: "external" });
      continue;
    }
    const from = cur?.tag ?? null;
    const adoption = component === "gateway" ? cur?.adoption : undefined;
    const withAdoption = adoption ? { gatewayAdoption: adoption } : {};
    if (adopt) actions.push({ component, reason: "adopt", from, to: img.tag, ...withAdoption });
    else if (from !== img.tag || adoption?.status === "pending") actions.push({ component, reason: "changed", from, to: img.tag, ...withAdoption });
    else skipped.push({ component, reason: "up-to-date" });
  }

  return { targetVersion: manifest.platform.tag, previousVersion: manifest.platform.previousVersion, actions, skipped };
}

// ---------------------------------------------------------------------------
// statusRows — pure presentation mapper for `m8t platform status`.
// Built ON TOP of diffPlan (no re-derived diff logic): one row per action
// (drift/needs-adopt) and one row per skipped entry (up-to-date/external/
// deferred — `only-filter` never appears here since status passes no
// `--only`). Read-only; callers must never call writeStamp with this data.
// ---------------------------------------------------------------------------

export interface StatusRow {
  component: ComponentKey;
  personaName?: string;
  installed: string;
  target: string;
  status: string;
}

export function statusRows(
  manifest: ReleaseManifest,
  stamp: InstalledStamp | null,
  tree: RepoTree,
  opts: { only?: ComponentKey; forceInfra?: boolean; skipInfra?: boolean } = {},
): StatusRow[] {
  const plan = diffPlan(manifest, stamp, tree, opts);
  const rows: StatusRow[] = [];

  for (const a of plan.actions) {
    rows.push({
      component: a.component,
      ...(a.personaName ? { personaName: a.personaName } : {}),
      installed: a.from ?? "(none)",
      target: a.to,
      status: a.reason === "adopt" ? "needs-adopt" : "drift",
    });
  }

  for (const s of plan.skipped) {
    rows.push({
      component: s.component,
      ...(s.personaName ? { personaName: s.personaName } : {}),
      installed: installedValueOf(stamp, s.component, s.personaName),
      target: targetValueOf(manifest, tree, s.component, s.personaName),
      status: s.reason,
    });
  }

  return rows;
}

function installedValueOf(stamp: InstalledStamp | null, component: ComponentKey, personaName?: string): string {
  if (!stamp) return "-";
  if (component === "infra") return stamp.components.infra.treeSha || "-";
  if (component === "personas") {
    // Indexed access into a Record is typed as always-present (no noUncheckedIndexedAccess),
    // but a persona genuinely absent from this stamp is real at runtime — check ownership.
    if (!personaName || !Object.hasOwn(stamp.components.personas, personaName)) return "-";
    return stamp.components.personas[personaName].treeSha;
  }
  if (component === "brainSeeds") return stamp.components.brainSeeds?.seedRev ?? "-";
  return stamp.components[component].tag;
}

function targetValueOf(manifest: ReleaseManifest, tree: RepoTree, component: ComponentKey, personaName?: string): string {
  if (component === "infra") return infraTargetSha(manifest, tree) || "-";
  if (component === "personas") {
    if (!personaName || !Object.hasOwn(manifest.components.personas.items, personaName)) return "-";
    return manifest.components.personas.items[personaName].treeSha;
  }
  if (component === "brainSeeds") return seedSetRev(manifest) || "-";
  return manifest.components[component].tag;
}

// ---------------------------------------------------------------------------
// applyPlan — the driver-agnostic orchestrator.
// ---------------------------------------------------------------------------

export interface ApplyCtx {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  contentDir: string;
  manifest: ReleaseManifest;
  onProgress?: (m: string) => void;
  /** The explicit `--suffix` for this run, when one was supplied. Required to
   *  stamp `system/infra-params` — there is no safe way to recover it otherwise. */
  suffix?: string;
}

export interface ConvergeDeps {
  /** Resolve a manifest persona key (for example `startup-advisor`) to the
   * deployed Foundry agent name (for example `stacey`) for health probes. */
  resolveAgentName?(personaName: string): string | undefined;
  applyGateway(a: ComponentAction, ctx: ApplyCtx): Promise<ImageStampEntry>;
  /** Restore an explicitly authorized, still-pending gateway adoption to its
   * exact source bytes. Used only by the same-platform compensation path: a
   * release-version rollback to the same target would otherwise roll forward
   * again and falsely report success. */
  restoreGatewayAdoption?(gateway: ImageStampEntry): Promise<ImageStampEntry>;
  applyAgent(a: ComponentAction, ctx: ApplyCtx): Promise<{ tag: string; digest: string } | "absent">;
  applyPersona(a: ComponentAction, ctx: ApplyCtx): Promise<{ treeSha: string; foundryVersion: string } | "noop" | "absent">;
  applyInfra(a: ComponentAction, ctx: ApplyCtx): Promise<{ treeSha: string }>;
  healthGate(ctx: ApplyCtx, applied: ComponentAction[]): Promise<void>;
  readStamp(): Promise<InstalledStamp | null>;
  writeStamp(s: InstalledStamp): Promise<void>;
  /** OPTIONAL: bring the deployed executor's outbound-email env into line with the intent
   *  recorded in the stamp.
   *
   *  Runs on EVERY converge, not per-action, because the failure it heals leaves the image
   *  perfectly correct: the canary's executor had a current image, a live verified ACS, and
   *  no `M8T_ACS_*` at all. `applyAgentImage` swaps only `image`, so an action-scoped hook
   *  would never fire for that install and the drift would persist forever.
   *
   *  Reconciles ENV ONLY — it must never provision ACS. The unattended rail does not create
   *  billable resources; provisioning stays in the CLI.
   *
   *  Takes NO arguments and reads the intent itself, so the same dep is callable from the
   *  no-drift path in `m8t platform update` — where there is no plan and no ctx. Gating it on
   *  a non-empty plan made it a dormant gate: the drift it heals leaves every component up to
   *  date, so it produces an EMPTY plan and the hook could never fire in the one state it
   *  exists for. */
  reconcileEmail?(): Promise<void>;
  /** OPTIONAL: stamp `system/infra-params` after a successful FULL converge
   *  (not `--only`) that ran with an explicit suffix. Both the CLI driver and the MI
   *  driver supply this, so both re-stamp on every full converge. */
  writeInfraParams?(ctx: ApplyCtx, suffix: string): Promise<void>;
  /** OPTIONAL (CLI-only in v1): open/update seed-refresh PRs against the install's brain
   *  repos. Absent on the MI driver → applyPlan skips brainSeeds honestly (no stamp advance).
   *  `converged` reports whether the pass genuinely reached a coherent state (Key Vault present
   *  AND no per-repo failures) — a degrade or any failure sets it false so applyPlan does NOT
   *  advance the seed stamp, and the pass is retried next converge (per-repo refresh is idempotent). */
  applySeedRefresh?(a: ComponentAction, ctx: ApplyCtx): Promise<{ delivered: number; noop: number; converged: boolean }>;
}

export interface ConvergeResult {
  stamp: InstalledStamp;
  applied: ComponentAction[];
}

/**
 * Walk `plan.actions` in order, dispatching each to the matching injected
 * executor, stamping progress (lastResult: "partial") after EVERY successful
 * component so a mid-plan failure leaves a resumable, honest record. After
 * all actions land, runs the health gate and stamps "success". Any throw
 * (from an executor or the health gate) stamps "failed" with the offending
 * component, writes best-effort, and rethrows.
 */
export async function applyPlan(plan: ApplyPlan, deps: ConvergeDeps, ctx: ApplyCtx): Promise<ConvergeResult> {
  const prior = await deps.readStamp();
  const stamp = seedStamp(prior, ctx.manifest, plan);
  // We're re-attempting — a prior FAILED stamp's lastError is stale the moment we
  // start a new pass; clear it so a mid-resume "partial" write doesn't show a stale error.
  delete stamp.lastError;
  const applied: ComponentAction[] = [];
  try {
    for (const a of plan.actions) {
      if (a.component === "infra") {
        stamp.components.infra = await deps.applyInfra(a, ctx);
      } else if (a.component === "gateway") {
        stamp.components.gateway = await deps.applyGateway(a, ctx);
      } else if (a.component === "codingAgent" || a.component === "azureExecutor") {
        const r = await deps.applyAgent(a, ctx);
        // "absent" — the hosted agent isn't deployed on this install; the executor did NOT
        // touch anything. Skip it ENTIRELY: don't record it in the stamp AND don't add it
        // to `applied` — the health gate probes `applied`, and probing a non-existent agent
        // would false-rollback an otherwise-good converge (gate only what
        // changed). An install without hosted agents must converge cleanly.
        if (r === "absent") {
          ctx.onProgress?.(`${a.component}: not deployed on this install — skipped (not gated).`);
          continue;
        }
        stamp.components[a.component] = { ...r, state: "managed" };
      } else if (a.component === "brainSeeds") {
        if (!deps.applySeedRefresh) {
          // Honest skip: this driver (e.g. the MI driver) doesn't carry the CLI-only-in-v1
          // seed-refresh executor. Don't stamp (would falsely claim delivery) and don't add
          // it to `applied` (never health-gated — brainSeeds isn't probed by the CLI's
          // healthGate anyway, but this keeps the invariant true for every driver).
          ctx.onProgress?.("brainSeeds: seed refresh is not supported by this driver (CLI-only in v1) — skipped.");
          continue;
        }
        const r = await deps.applySeedRefresh(a, ctx);
        ctx.onProgress?.(`brainSeeds: delivered to ${r.delivered.toString()} repo(s), ${r.noop.toString()} already current.`);
        if (!r.converged) {
          // The pass did NOT reach a coherent state (no Key Vault, or some repos failed).
          // Advancing the stamp here would falsely mark seeds delivered and cause the next
          // `m8t platform update` to SKIP brainSeeds — silently suppressing founder PRs.
          // Don't stamp and don't add to `applied` (same shape as the absent-executor branch);
          // the idempotent per-repo refresh is retried on the next converge.
          ctx.onProgress?.("brainSeeds: not fully delivered (no Key Vault, or some repos failed) — stamp NOT advanced; will retry next converge.");
          continue;
        }
        stamp.components.brainSeeds = { seedRev: a.to };
      } else {
        // a.component === "personas" (the only ComponentKey left after the branches above).
        // Defensive: diffPlan never emits a personas action without a personaName, but a
        // malformed action must fail loudly here rather than being silently applied+stamped
        // while dispatching nothing.
        if (!a.personaName) {
          throw new LocalCliError({ code: "PLATFORM_PERSONA_NO_NAME", message: "persona action missing personaName" });
        }
        const r = await deps.applyPersona(a, ctx);
        if (r === "absent") {
          // The persona isn't deployed on this install — nothing was touched. Skip it
          // ENTIRELY (out of the stamp AND out of `applied`, so it is never gated — same
          // reasoning as the absent hosted-agent branch above).
          ctx.onProgress?.(`persona '${a.personaName}': not deployed on this install — skipped (not gated).`);
          continue;
        }
        // Indexed access into a Record is typed as always-present (no noUncheckedIndexedAccess),
        // but a persona new to this stamp is genuinely absent at runtime — check ownership explicitly.
        const prev: PersonaStampEntry | undefined = Object.hasOwn(stamp.components.personas, a.personaName)
          ? stamp.components.personas[a.personaName]
          : undefined;
        stamp.components.personas[a.personaName] = r === "noop" ? { treeSha: a.to, foundryVersion: prev?.foundryVersion ?? "" } : r;
      }
      applied.push(a);
      stamp.lastResult = "partial";
      stamp.updatedAt = new Date().toISOString();
      await deps.writeStamp(stamp); // incremental → resumable
    }
    // Runs for every converge that reaches here, including one where no action touched the
    // executor. NOTE: the no-drift case never reaches applyPlan at all — `m8t platform update`
    // calls this same dep before its empty-plan early return.
    if (deps.reconcileEmail) {
      await deps.reconcileEmail();
    }
    await deps.healthGate(ctx, applied);
    stamp.lastResult = "success";
    delete stamp.lastError;
    // Advance the platform version ONLY on a genuine full-converge success — never at seed
    // time (see seedStamp) and never for a `--only`-restricted run, which by definition
    // didn't converge every component and so cannot honestly claim the whole platform version.
    const isTargeted = plan.skipped.some((s) => s.reason === "only-filter");
    if (!isTargeted && plan.targetVersion !== stamp.platformVersion) {
      stamp.previousPlatformVersion = stamp.platformVersion;
      stamp.platformVersion = plan.targetVersion;
    }
    stamp.updatedAt = new Date().toISOString();
    await deps.writeStamp(stamp);
    // Stamp system/infra-params ONLY on a genuine full-converge success (never
    // for an `--only`-restricted run — same guard as the platformVersion advance above) and
    // ONLY when this run had an explicit suffix to stamp. Both conditions must hold: a
    // partial run doesn't speak for the whole install, and a run with no suffix has nothing
    // trustworthy to record (see resolveBicepParamsForConverge's refusal for the same reason).
    if (!isTargeted && ctx.suffix && deps.writeInfraParams) {
      await deps.writeInfraParams(ctx, ctx.suffix);
    }
    return { stamp, applied };
  } catch (e) {
    stamp.lastResult = "failed";
    stamp.lastError = {
      component: applied.length < plan.actions.length ? plan.actions[applied.length].component : "healthGate",
      message: (e as Error).message,
    };
    stamp.updatedAt = new Date().toISOString();
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- best-effort write on the failure path; the original error always wins (rethrown below)
    await deps.writeStamp(stamp).catch(() => {});
    throw e;
  }
}

function seedStamp(prior: InstalledStamp | null, manifest: ReleaseManifest, plan: ApplyPlan): InstalledStamp {
  const blankImg = (): ImageStampEntry => ({ tag: "", digest: "", state: "managed" });
  // Prior-reuse: do NOT advance platformVersion/previousPlatformVersion at seed time.
  // A re-run at the SAME target must not overwrite previousPlatformVersion (that would
  // destroy the rollback target), and a --only/--force-infra run must not claim the whole
  // platform version for a partial converge. Versions are only advanced by applyPlan's
  // SUCCESS write, and only for a full (non --only) converge — see there.
  return prior
    ? { ...prior }
    : {
        schemaVersion: 1,
        platformVersion: plan.targetVersion,
        previousPlatformVersion: plan.previousVersion,
        updatedAt: new Date().toISOString(),
        lastResult: "partial",
        cli: "",
        components: {
          gateway: blankImg(),
          codingAgent: blankImg(),
          azureExecutor: blankImg(),
          personas: {},
          infra: { treeSha: "" },
          brainSeeds: { seedRev: "" },
        },
      };
}

// ---------------------------------------------------------------------------
// Concrete gateway executor. Wired by the CLI driver
// (a later task) into ConvergeDeps.applyGateway.
// ---------------------------------------------------------------------------

/**
 * Roll the gateway Container App onto the manifest's pinned release, BY DIGEST,
 * reusing the same BYOC-refusal + tag-resolution logic as `m8t platform update`. A
 * BYOC (private-ACR) image is never touched — it's marked "external" so future
 * diffs skip it rather than re-attempting a roll every converge.
 *
 * The manifest's digest is passed to planUpdate as well as used for the roll: a
 * gateway this rail has already pinned carries no tag, so digest equality is the
 * only thing that can decide "already at target". Without it the plan reads "roll"
 * on every tick and this function churns a fresh revision forever.
 */
export async function applyGatewayImage(
  a: ComponentAction,
  ctx: ApplyCtx,
  gatewayResourceId: string,
): Promise<ImageStampEntry> {
  const digest = ctx.manifest.components.gateway.digest;
  if (a.gatewayAdoption) {
    const adoption = await rollAdoptedGateway({
      gatewayResourceId,
      adoption: a.gatewayAdoption,
      targetTag: a.to,
      targetDigest: assertGatewayDigest(ctx.manifest.components.gateway),
      onProgress: ctx.onProgress,
    });
    return { tag: a.to, digest, state: "managed", adoption };
  }
  const { resourceGroup, name } = parseContainerAppResourceId(gatewayResourceId);
  const current = (
    await runAz(["containerapp", "show", "-g", resourceGroup, "-n", name, "--query", "properties.template.containers[0].image", "-o", "tsv"])
  ).trim();
  const tags = await fetchPublicTags(DEFAULT_IMAGE_REPO);
  const plan = planUpdate({ currentImage: current, availableTags: tags, imageRepo: DEFAULT_IMAGE_REPO, to: a.to, toDigest: digest });
  switch (plan.kind) {
    case "refuse-byoc":
      ctx.onProgress?.(`gateway is BYOC (${plan.currentRepo}) — marking externally managed.`);
      return { tag: a.to, digest, state: "external" };
    case "roll":
      // plan.repo is the TRACKED repo (DEFAULT_IMAGE_REPO), deliberately not the
      // manifest's ref — that is the pre-existing repo semantics of this roll. Only
      // the binding changes: the digest replaces the tag. `tag` in the returned
      // record stays a tag; it feeds the stamp's version ladder, which the web
      // update-availability check settles against.
      await runAz(["containerapp", "update", "-n", name, "-g", resourceGroup, "--image", `${plan.repo}@${assertGatewayDigest(ctx.manifest.components.gateway)}`]);
      return { tag: a.to, digest, state: "managed" };
    case "noop":
      // Crash-resume safe path: the live image is genuinely already at the target
      // (e.g. a prior run rolled it but the stamp write didn't land) — no update needed.
      return { tag: a.to, digest, state: "managed" };
    case "tag-not-found":
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_TAG_UNAVAILABLE",
        message: `Gateway image tag '${a.to}' is not published on the tracked repo — cannot converge the gateway.`,
        hint: "The manifest may be ahead of the registry (propagation lag), or the repo is wrong. Retry shortly or check --image-repo.",
      });
    case "no-versions":
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_NO_VERSIONS",
        message: "No published gateway image versions found on the tracked repo.",
      });
  }
}

// ---------------------------------------------------------------------------
// Concrete infra executor. Wired by the CLI driver
// (a later task) into ConvergeDeps.applyInfra.
// ---------------------------------------------------------------------------

/**
 * Non-forced infra actions (adopt / changed-but-deferred) are record-only:
 * the installer's own converge job owns the real bicep apply (see diffPlan's
 * comment on why reconstructing BicepParams outside that path is risky).
 * `--force-infra` (reason: "forced") runs a real idempotent bicep deploy.
 */
export async function applyInfraDeploy(
  a: ComponentAction,
  ctx: ApplyCtx,
  target: string,
  opts: ResolveBicepParamsOpts = {},
): Promise<{ treeSha: string }> {
  if (a.reason !== "forced") {
    ctx.onProgress?.(`infra baseline recorded at ${target} (not re-provisioned; apply with --force-infra or the update job).`);
    return { treeSha: target };
  }
  ctx.onProgress?.("converging infra (idempotent bicep deploy)…");
  const params = await resolveBicepParamsForConverge(ctx, opts);
  await runBicepDeployment({
    resourceGroup: ctx.resourceGroup,
    repoRoot: ctx.contentDir,
    params: buildBicepParams(params),
    deploymentName: `m8t-converge-${target.slice(0, 12)}`,
  });
  return { treeSha: target };
}

export interface ResolveBicepParamsOpts {
  /** REQUIRED for a real deploy — recovering it wrong provisions duplicate resources. */
  suffix?: string;
  tenantId?: string;
  clientId?: string;
  foundryEndpoint?: string;
  foundryResourceId?: string;
  acrPullIdentity?: string;
  acrResourceId?: string;
  foundryTracingMode?: FoundryTracingMode;
  /** Forwarded to main.bicep. Unattended converges set false so they preserve
   *  the founder-owned cross-scope Foundry tracing connection. */
  deployFoundryTracingConnection?: boolean;
  /** Forwarded to main.bicep. The MI updater's converge sets false (RG-scoped —
   *  cannot deploy the subscription-scoped module); the founder path leaves it undefined. */
  assignSubscriptionRoles?: boolean;
  /** The live install's voice-relay shared secret, read back from the deployed gateway.
   *  Omitted ⇒ main.bicep's newGuid() default re-mints it and rolls gateway + relay. */
  voiceInternalSecret?: string;
  /** Release-channel URL stamped in system/infra-params. Re-emitted so an infra
   *  converge does not drop the gateway's M8T_UPDATE_CHANNEL_URL env. */
  channelUrl?: string;
  brainCodifyAudience?: string;
  brainCodifyAllowlist?: string;
  brainCodifyWorkflowRef?: string;
  brainCodifyModel?: string;
  brainCodifyEvalModel?: string;
  /** Durable scheduler posture from system/infra-params. */
  dreamerEnabled?: string;
  /** Complete Teaching posture from system/infra-params. */
  teachingEnabled?: string;
  teachingModel?: string;
  teachingServiceAppIds?: string;
  teachingRetentionDays?: number;
  /** The install's real region, stamped in system/infra-params. An explicit value here
   *  wins over the `az resource list … [0].location` derivation below — that derivation
   *  picks an arbitrary resource's region, which can disagree with the install's actual
   *  region (the same reasoning `m8t platform enable-auto-update` already applies).
   *  Falls back to the derivation only when the row predates this field. */
  location?: string;
}

/**
 * Assemble the 9-field `BicepParams` set for a converge-triggered infra
 * deploy, reusing the same resolvers `m8t deploy` uses. `suffix` MUST come
 * from an explicit `--suffix` flag (surfaced here via `opts.suffix`) — there
 * is no safe way to recover it from live state, and guessing wrong would
 * provision duplicate resources alongside the real deployment.
 *
 * DO NOT ADD `installerImage` HERE without reading this first.
 *
 * Omitting it is load-bearing, not an oversight. main.bicep gates the updater on
 * `provisionUpdaterEffective = provisionUpdater && !empty(installerImage)`, so
 * leaving it empty means the converge does not deploy the updater module at all
 * — which is what lets the engine's self-update hop survive the converge that
 * follows it.
 *
 * Set it to anything other than the manifest's own installer image and the two
 * fight: the engine hops to the manifest's image, the converge's bicep run
 * immediately resets the job to this one, and the next tick hops again. Forever,
 * on every tick, while looking healthy. `m8t deploy --installer-image` exists
 * (added for the infra-drift comparison, which must describe the updater it can
 * see) and is a perfectly good flag — it just must never be wired through here
 * with a value the manifest did not choose.
 */
export async function resolveBicepParamsForConverge(
  ctx: ApplyCtx,
  opts: ResolveBicepParamsOpts = {},
): Promise<BicepParams> {
  if (!opts.suffix) {
    throw new LocalCliError({
      code: "PLATFORM_INFRA_SUFFIX_REQUIRED",
      message: "A --force-infra converge deploy requires an explicit --suffix.",
      hint: "Pass --suffix <existing-suffix> (from the deployed resource names) to avoid provisioning duplicate resources.",
    });
  }

  // A stamped location wins — see ResolveBicepParamsOpts.location. Only derive from an
  // arbitrary resource's region when the row predates that field (older installs).
  const location =
    opts.location ??
    (await runAz(["resource", "list", "-g", ctx.resourceGroup, "--query", "[0].location", "-o", "tsv"])).trim();

  const foundryResourceId = await resolveFoundryResourceId(opts.foundryEndpoint ?? "", opts.foundryResourceId);
  const acrPullIdentityResourceId = resolveAcrPullIdentity({ explicit: opts.acrPullIdentity });
  // The infra converge is IMAGE-NEUTRAL: it redeploys the whole stack, and the
  // gateway's image is not its business. `applyGatewayImage` owns that roll, and
  // it deliberately refuses a BYOC/private-ACR gateway (marking it externally
  // managed) instead of dragging it onto the tracked public repo.
  //
  // This used to pass the manifest's ref unconditionally, which silently
  // overrode that refusal — and worse, broke outright on any install whose
  // container app authenticates to its registry with a managed identity: Azure
  // rejects a non-Azure registry in that combination
  // (ContainerAppRegistryServerNotAllowedForManagedIdentity), so the ENTIRE
  // infra deployment failed, not just the gateway. Live on m8t's own canary,
  // 2026-08-11: a manifest pinning ghcr.io against an ACR-backed container app.
  //
  // So: preserve whatever the gateway is actually running. The manifest's ref is
  // the fallback for a stack with no live gateway to read (a from-zero deploy),
  // where it is also the only correct answer. Still digest-bound in that case —
  // see gatewayRefFromComponent; a tag would re-open the mutable-tag exposure on
  // the one path that redeploys everything.
  const imageRef = gatewayRefFromComponent(ctx.manifest.components.gateway);
  // `imageRef` stays the manifest's: the VOICE RELAY shares this param and its
  // only update path is this deploy, so pinning it to live state would freeze it
  // one release behind the gateway forever. The gateway gets its own override.
  const gatewayName = `m8t-gateway-${opts.suffix}`;
  const live = await readLiveGatewayImage({
    gatewayName,
    resourceGroup: ctx.resourceGroup,
    subscriptionId: ctx.subscriptionId,
  });
  // A failed read is NOT "no gateway". Both callers of this function prove a
  // gateway container app exists before reaching here — the updater via
  // resolveGatewayResourceId (which throws PLATFORM_GATEWAY_NOT_FOUND), the
  // founder path via resolveGatewayContext — so an error here is a throttle, a
  // 403 or an ARM blip. Falling back on one would silently hand bicep the
  // manifest's ref and re-point a live gateway: exactly the failure this whole
  // change exists to stop. Refuse; a refused converge lands terminal and retries.
  if ("error" in live) {
    throw new LocalCliError({
      code: "PLATFORM_GATEWAY_IMAGE_UNREADABLE",
      message: `Could not read the live image of '${gatewayName}' in ${ctx.resourceGroup}: ${live.error}`,
      hint: "An infra converge must not guess the gateway's image — deploying the manifest's ref would re-point it. Re-run the converge; if this persists, check the updater identity's read access to the container app.",
    });
  }
  if (live.image && live.image !== imageRef) {
    ctx.onProgress?.(`infra converge preserves the live gateway image (${live.image}); rolling it is applyGatewayImage's decision, not this deploy's.`);
  }
  const acrResourceId = await resolveAcrResourceId({ imageRef: live.image || imageRef, explicit: opts.acrResourceId });

  return {
    location,
    suffix: opts.suffix,
    imageRef,
    // Only when there IS a live gateway to preserve. Empty on a stack whose
    // gateway app exists but reports no image, where the manifest ref is the
    // only answer available.
    ...(live.image ? { gatewayImageRef: live.image } : {}),
    tenantId: opts.tenantId ?? "",
    clientId: opts.clientId ?? "",
    foundryResourceId,
    foundryProjectEndpoint: opts.foundryEndpoint ?? "",
    acrPullIdentityResourceId,
    acrResourceId,
    foundryTracingMode: opts.foundryTracingMode,
    ...(opts.deployFoundryTracingConnection !== undefined
      ? { deployFoundryTracingConnection: opts.deployFoundryTracingConnection }
      : {}),
    ...(opts.assignSubscriptionRoles !== undefined ? { assignSubscriptionRoles: opts.assignSubscriptionRoles } : {}),
    ...(opts.voiceInternalSecret ? { voiceInternalSecret: opts.voiceInternalSecret } : {}),
    ...(opts.channelUrl ? { channelUrl: opts.channelUrl } : {}),
    ...(opts.brainCodifyAudience !== undefined ? { brainCodifyAudience: opts.brainCodifyAudience } : {}),
    ...(opts.brainCodifyAllowlist !== undefined ? { brainCodifyAllowlist: opts.brainCodifyAllowlist } : {}),
    ...(opts.brainCodifyWorkflowRef !== undefined ? { brainCodifyWorkflowRef: opts.brainCodifyWorkflowRef } : {}),
    ...(opts.brainCodifyModel !== undefined ? { brainCodifyModel: opts.brainCodifyModel } : {}),
    ...(opts.brainCodifyEvalModel !== undefined ? { brainCodifyEvalModel: opts.brainCodifyEvalModel } : {}),
    ...(opts.dreamerEnabled !== undefined ? { dreamerEnabled: opts.dreamerEnabled } : {}),
    ...(opts.teachingEnabled !== undefined ? { teachingEnabled: opts.teachingEnabled } : {}),
    ...(opts.teachingModel !== undefined ? { teachingModel: opts.teachingModel } : {}),
    ...(opts.teachingServiceAppIds !== undefined ? { teachingServiceAppIds: opts.teachingServiceAppIds } : {}),
    ...(opts.teachingRetentionDays !== undefined ? { teachingRetentionDays: opts.teachingRetentionDays } : {}),
  };
}

// ---------------------------------------------------------------------------
// Concrete hosted-agent executor. Wired by the CLI driver
// (a later task) into ConvergeDeps.applyAgent for codingAgent/azureExecutor.
// ---------------------------------------------------------------------------

/**
 * Clone a hosted agent's definition and replace ONLY the top-level `image`
 * field, leaving cpu/memory/env/tools/container_protocol_versions untouched.
 * Pure — no I/O. Exported for direct unit testing.
 */
export function swapHostedImage(def: AgentDefinition, newImage: string): AgentDefinition {
  return { ...def, image: newImage };
}

/**
 * Roll a hosted agent (coder / ezra-executor) onto the manifest-pinned
 * image: read the live version, stage the public GHCR image into the
 * customer's ACR (a no-op for an already-ACR ref), swap only the image
 * field, and POST a new version with the live metadata (a2a / brain markers)
 * carried through unchanged.
 */
export async function applyAgentImage(
  a: ComponentAction,
  ctx: ApplyCtx,
  opts: { agentName: string; project: FoundryProject; ghcrImage: string },
): Promise<{ tag: string; digest: string }> {
  const current = await getAgentVersion({ credential: ctx.credential, projectEndpoint: opts.project.endpoint, agentName: opts.agentName });
  const staged = await stagePublicImageIfNeeded({ image: opts.ghcrImage, project: opts.project, onProgress: ctx.onProgress });
  const definition = swapHostedImage(current.definition, staged.image);
  await createAgentVersion({
    credential: ctx.credential,
    projectEndpoint: opts.project.endpoint,
    agentName: opts.agentName,
    definition,
    metadata: current.metadata ?? {},
    extraHeaders: { "Foundry-Features": "HostedAgents=V1Preview" },
  });
  const digest = a.component === "codingAgent" ? ctx.manifest.components.codingAgent.digest : ctx.manifest.components.azureExecutor.digest;
  return { tag: a.to, digest };
}

// ---------------------------------------------------------------------------
// Concrete persona executor. Wired by the CLI driver (a later task)
// into ConvergeDeps.applyPersona.
// ---------------------------------------------------------------------------

interface DeclaredFillableField {
  name: string;
  default?: string;
}

function readPersonaFrontmatter(personaPath: string): PersonaFrontmatter | null {
  let raw: string;
  try {
    raw = fs.readFileSync(personaPath, "utf8");
  } catch {
    return null;
  }
  // \r?\n tolerates CRLF line endings (Windows git checkouts) as well as LF.
  const fmMatch = /^---\r?\n([\s\S]*?)\r?\n---/.exec(raw);
  if (!fmMatch) return null;
  try {
    return (parseYaml(fmMatch[1]) ?? {}) as PersonaFrontmatter;
  } catch {
    return null;
  }
}

/** Parse the persona frontmatter's `fillable-fields` list. Empty when absent/unparseable. */
export function declaredFillableFields(personaPath: string): DeclaredFillableField[] {
  return readPersonaFrontmatter(personaPath)?.["fillable-fields"] ?? [];
}

type PersonaTool = NonNullable<AgentDefinition["tools"]>[number];

/**
 * Brain and A2A are attached after persona deployment and carry live connection
 * ids/bearers. Everything else in a prompt agent's tool list is persona-owned:
 * converge replaces that base set from the pinned persona, then reapplies the
 * two live overlays byte-for-byte.
 */
function isRuntimePersonaTool(tool: PersonaTool): boolean {
  if (tool.type !== "mcp") return false;
  const label = (tool as { server_label?: unknown }).server_label;
  return label === "brain" || label === "a2a";
}

/** Replace persona-owned tools while preserving connection-backed runtime overlays. */
export function convergePersonaTools(
  currentTools: AgentDefinition["tools"],
  declaredTools: readonly Record<string, unknown>[] | undefined,
): NonNullable<AgentDefinition["tools"]> {
  // Runtime labels are reserved even if a malformed persona declares them: the
  // pin must never replace live connection descriptors with static content.
  const personaTools = (declaredTools ?? [])
    .map((tool) => tool as PersonaTool)
    .filter((tool) => !isRuntimePersonaTool(tool));
  const runtimeTools = (currentTools ?? []).filter(isRuntimePersonaTool);
  return [...personaTools, ...runtimeTools];
}

export type PersonaPostCreateMismatch = "version" | "definition" | "tools" | "metadata";

/**
 * Compare the just-created version with the exact candidate the converge sent.
 * Tool comparison tolerates only Foundry's documented echo normalization
 * (`strict: null`, MCP `allowed_tools` envelope); all other differences fail.
 */
export function personaPostCreateMismatches(
  candidate: { definition: AgentDefinition; metadata: Record<string, string> },
  actual: AgentVersionRecord,
  expectedVersion: string,
): PersonaPostCreateMismatch[] {
  const mismatches: PersonaPostCreateMismatch[] = [];
  if (actual.version !== expectedVersion) mismatches.push("version");

  const { tools: expectedTools, ...expectedDefinition } = candidate.definition;
  const { tools: actualTools, ...actualDefinition } = actual.definition;
  if (!equivalentToolDefinition(actualDefinition, expectedDefinition)) mismatches.push("definition");
  if (!equivalentToolDefinition(actualTools ?? [], expectedTools ?? [])) mismatches.push("tools");
  if (!equivalentToolDefinition(actual.metadata ?? {}, candidate.metadata)) mismatches.push("metadata");
  return mismatches;
}

/** `{ [name]: default }` for declared fields that carry a default. */
export function defaultsOf(declared: DeclaredFillableField[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of declared) {
    if (f.default !== undefined) out[f.name] = f.default;
  }
  return out;
}

/** Render the persona's base body (frontmatter stripped, placeholders substituted, self-ID injected). */
export function injectAndRender(personaPath: string, values: Record<string, string>, agentName: string): string {
  return renderPersonaBody(personaPath, values, agentName);
}

/** Read the loader file's text, or null when no loaderPath is configured. */
export function loaderText(loaderPath: string | null): string | null {
  if (!loaderPath) return null;
  try {
    return fs.readFileSync(loaderPath, "utf8");
  } catch {
    return null;
  }
}

/**
 * Recover the brain repo from `metadata.brain` (a JSON-encoded BrainLink object).
 * CRITICAL: `composeInstructions` templates the loader with `{{brain_repo}}` — a
 * brain-linked persona that fails to recover its repo here would leak the literal
 * placeholder into deployed instructions.
 */
export function brainRepoOf(current: AgentVersionRecord): string | null {
  const raw = current.metadata?.brain;
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw) as { repo?: string };
    // Deliberately `||`, not `??`: an empty-string `repo` is as unusable as a missing one
    // and must also fall back to null (composeInstructions' no-substitution path), rather
    // than being passed through and risking a leaked literal `{{brain_repo}}`.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must also be treated as absent
    return parsed.repo || null;
  } catch {
    return null;
  }
}

/**
 * Re-version a deployed prompt persona at the release rev: read the live agent,
 * recover its instance fillable-field values (metadata → yaml), render the new
 * base body from the release-rev persona file, and recompose it preserving
 * whatever overlays (a2a snippet, brain loader) the live agent carried. Skips
 * the network round-trip when the candidate is byte-identical to live, and
 * refuses to silently revert unrecoverable instance values to their defaults.
 */
export async function applyPersona(
  a: ComponentAction,
  ctx: ApplyCtx,
  opts: { agentName: string; project: FoundryProject; personaDir: string; loaderPath: string | null },
): Promise<{ treeSha: string; foundryVersion: string } | "noop"> {
  const current = await getAgentVersion({ credential: ctx.credential, projectEndpoint: opts.project.endpoint, agentName: opts.agentName });
  const personaPath = path.join(opts.personaDir, "persona.md");
  const yamlValues = readAgentYaml(opts.agentName)?.fillableFieldValues ?? null;
  const values = resolveFillableValues(current, yamlValues);

  const frontmatter = readPersonaFrontmatter(personaPath);
  const declared = frontmatter?.["fillable-fields"] ?? [];
  const tools = convergePersonaTools(
    current.definition.tools,
    personaFoundryTools(a.personaName ?? opts.agentName, frontmatter ?? {}),
  );

  // Unrecoverable-revert guard: fields are declared, nothing was recovered — check whether
  // rendering from defaults would actually CHANGE live instructions. If it matches live,
  // no instance values were ever set and defaulting is safe; if it differs, re-versioning
  // would silently revert real instance data, so refuse instead.
  if (declared.length > 0 && values === null) {
    const defaultBody = injectAndRender(personaPath, defaultsOf(declared), opts.agentName);
    const candidateInstructions = composeInstructions({
      baseBody: defaultBody,
      current,
      loaderText: loaderText(opts.loaderPath),
      brainRepo: brainRepoOf(current),
    });
    if (candidateInstructions !== (current.definition.instructions ?? "")) {
      throw new LocalCliError({
        code: "PLATFORM_PERSONA_UNRECOVERABLE",
        message: `Cannot re-version '${opts.agentName}' — its instance field values are not recoverable (would revert to defaults). Re-deploy the advisor, or pass --content-dir with the correct values.`,
      });
    }
  }

  const missing = declared.filter((f) => !values?.[f.name] && f.default === undefined);
  if (missing.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_PERSONA_FIELD_MISSING",
      message: `Persona '${a.personaName ?? opts.agentName}' needs values for: ${missing.map((f) => f.name).join(", ")}.`,
    });
  }

  // Defaults UNDER the recovered values, not either/or. The `missing` guard above
  // deliberately tolerates a field absent from `values` when it declares a
  // default — and then this line used to render with `values` alone, so that
  // tolerance produced a literal `{{placeholder}}` instead of the default. The
  // two lines disagreed about what a default is for.
  //
  // It bites whenever a live agent's recovered `metadata.fillableFieldValues` is
  // PARTIAL, which is the normal state after a release adds a fillable field: the
  // agent was deployed before the field existed. Healing that is exactly what a
  // converge is for. m8t's canary hit it on 2026-08-11 (startup-advisor-intake,
  // `chosen_model_note`), and every converge-managed persona is equally exposed —
  // the first release that adds a defaulted field to `cmo` reproduces it.
  const baseBody = injectAndRender(personaPath, { ...defaultsOf(declared), ...(values ?? {}) }, opts.agentName);
  const instructions = composeInstructions({
    baseBody,
    current,
    loaderText: loaderText(opts.loaderPath),
    brainRepo: brainRepoOf(current),
  });
  const metadata: Record<string, string> = {
    ...(current.metadata ?? {}),
    persona: a.personaName ?? opts.agentName,
    // Re-asserted on every converge, not just at initial deploy — heals an install whose
    // advisor was deployed by a CLI that predates the roster-visibility stamp (see
    // deploy-prompt-advisor.ts). Derived from the deployed agent name, same as there.
    productVisibility: productVisibilityFor(opts.agentName),
    ...(values ? { fillableFieldValues: JSON.stringify(values) } : {}),
  };
  // The hydrated/pinned persona is the content source of truth. A converge that
  // updates its body/tools but leaves the old metadata version makes the rollout
  // look stale and defeats later contract audits. Match initial deploy semantics:
  // absent version means absent metadata, not preservation of a stale value.
  if (frontmatter?.version === undefined) delete metadata.personaVersion;
  else metadata.personaVersion = String(frontmatter.version);
  const candidate = { definition: { ...current.definition, instructions, tools }, metadata };
  if (isNoop(candidate, current)) return "noop";
  const foundryVersion = await createAgentVersion({
    credential: ctx.credential,
    projectEndpoint: opts.project.endpoint,
    agentName: opts.agentName,
    definition: candidate.definition,
    metadata,
  });
  const applied = await getAgentVersion({
    credential: ctx.credential,
    projectEndpoint: opts.project.endpoint,
    agentName: opts.agentName,
  });
  const mismatches = personaPostCreateMismatches(candidate, applied, foundryVersion);
  if (mismatches.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_PERSONA_VERIFY_FAILED",
      message:
        `Foundry did not persist the converged persona '${a.personaName ?? opts.agentName}' exactly ` +
        `(mismatch: ${mismatches.join(", ")}); refusing to stamp tree ${a.to}.`,
      hint: "Re-run the converge after Foundry reports the created version as latest; persistent mismatch requires platform investigation.",
    });
  }
  return { treeSha: a.to, foundryVersion };
}
